import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addPatientAllergy } from '../../../Redux/AllergySlice';
import { toast } from 'react-toastify';

const AddAllergyModal = ({ isOpen, onClose, patientId, hasNoKnownAllergies }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [currentAllergy, setCurrentAllergy] = useState({
    name: '',
    onset_date: '',
    reaction: ''
  });
  const dispatch = useDispatch()
  
  // Reset form when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      // Reset allergy form
      setCurrentAllergy({
        name: '',
        onset_date: '',
        reaction: ''
      });
      setIsSaving(false);
    }
  }, [isOpen]);

  // Handle adding the allergy
  const handleAddAllergy = async () => {
    if (!patientId) {
        console.error('Patient ID is missing.');
        return;
    }
    
    // Prevent adding allergies if the patient already has "No Known Allergies" set
    if (hasNoKnownAllergies) {
        toast.warning('The patient already has "No Known Allergies" status. You must remove this status before adding specific allergies.');
        return;
    }
    
    try {
        setIsSaving(true);
        
        // Validate regular allergy entries
        if (!currentAllergy.name || !currentAllergy.reaction || !currentAllergy.onset_date) {
            toast.error('Please fill in all allergy fields');
            setIsSaving(false);
            return;
        }
        
        await dispatch(
            addPatientAllergy({
                allergies: [currentAllergy],
                patientId,
            })
        ).unwrap();
        onClose();
    } catch (error) {
        console.error('Failed to add allergy:', error);
        
        // Enhance error handling with more detailed messages
        if (error.status === 422) {
            toast.error('Validation error: Please check all fields');
        } else if (error.message) {
            toast.error(`Error: ${error.message}`);
        } else {
            toast.error('Failed to add allergy');
        }
    } finally {
        setIsSaving(false);
    }
  };
  
  // If modal is not open, return null
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="mb-6 text-center">
          <h2 className="text-xl font-semibold text-gray-800">Add New Allergy</h2>
          <p className="text-sm text-gray-600 mt-1">Enter details for the new allergy</p>
        </div>

        {hasNoKnownAllergies ? (
          <div className="mb-6 p-3 bg-gray-50 rounded border border-gray-200">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Note:</span> This patient already has "No Known Allergies" status. 
              You must remove that status first before adding specific allergies.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="name" className="block font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input 
                id="name"
                value={currentAllergy.name}
                onChange={(e) => setCurrentAllergy({...currentAllergy, name: e.target.value})}
                className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter allergy name"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="onset_date" className="block font-medium text-gray-700">
                Onset Date <span className="text-red-500">*</span>
              </label>
              <input 
                id="onset_date"
                type="date"
                max={new Date().toISOString().split("T")[0]}
                value={currentAllergy.onset_date}
                onChange={(e) => setCurrentAllergy({...currentAllergy, onset_date: e.target.value})}
                className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="reaction" className="block font-medium text-gray-700">
                Reaction <span className="text-red-500">*</span>
              </label>
              <textarea 
                id="reaction"
                value={currentAllergy.reaction}
                onChange={(e) => setCurrentAllergy({...currentAllergy, reaction: e.target.value})}
                className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Describe the allergic reaction"
                rows="3"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end mt-8 space-x-3">
          <button 
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-2 border-red-300"
          >
            Cancel
          </button>
          <button 
            onClick={handleAddAllergy}
            disabled={isSaving || hasNoKnownAllergies}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Adding...' : 'Add Allergy'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAllergyModal;

// Example usage in a parent component:
// function ParentComponent() {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [allergies, setAllergies] = useState([]);
// 
//   const handleAddAllergy = (newAllergy) => {
//     setAllergies([...allergies, newAllergy]);
//   };
// 
//   return (
//     <>
//       <button onClick={() => setIsModalOpen(true)}>Add Allergy</button>
//       <AddAllergyModal 
//         isOpen={isModalOpen} 
//         onClose={() => setIsModalOpen(false)}
//         onAddAllergy={handleAddAllergy}
//       />
//     </>
//   );
// }